import { createSnack } from 'snackbar/snackbarSlice';
import { createSingleAbortableQuery } from 'utils/helpers/abortPreviousRequest';
import { createCacheUpdateHandler } from 'utils/helpers/fetch';
import { loadingManagementApi } from '../../loadingManagementApi';

const getUrl = suffix => `customer-authorizations/${suffix}`;

const remapParams = params => {
  const { pageSize, ...rest } = params || {};
  return pageSize !== undefined ? { ...rest, results: pageSize } : rest;
};

const customerAuthListController = { current: null };

const parseToggleActivationResponse = async response => {
  const responseText = await response.text();

  if (!responseText) {
    return '';
  }

  try {
    return JSON.parse(responseText);
  } catch {
    return responseText;
  }
};

const customerAuthGridEndpoints = loadingManagementApi.injectEndpoints({
  endpoints: ({ mutation, query }) => ({
    // Bulk actions submit all selected authorization IDs in one request.
    // Keep these mutations separate from the single-row toggle below.
    bulkActivateCustomerAuthorizations: mutation({
      invalidatesTags: ['CustomerAuthorizationList'],
      query: ({ customerAuthorizationIds }) => ({
        body: { customerAuthorizationIds },
        method: 'POST',
        responseHandler: parseToggleActivationResponse,
        url: getUrl('activate-customer-authorizations'),
      }),
    }),
    bulkDeactivateCustomerAuthorizations: mutation({
      invalidatesTags: ['CustomerAuthorizationList'],
      query: ({ customerAuthorizationIds }) => ({
        body: { customerAuthorizationIds },
        method: 'POST',
        responseHandler: parseToggleActivationResponse,
        url: getUrl('deactivate-customer-authorizations'),
      }),
    }),
    getCustomerAuthorizationFilterValues: query({
      query: ({ params }) => ({
        method: 'GET',
        params,
        url: getUrl('get-customer-authorization-filter-values'),
      }),
    }),
    getCustomerAuthorizationsExportList: query({
      query: params => ({
        params,
        responseHandler: response => response.blob(),
        url: getUrl('export'),
      }),
    }),
    getCustomerAuthorizationsList: query({
      providesTags: ['CustomerAuthorizationList'],
      queryFn: createSingleAbortableQuery(
        customerAuthListController,
        async ({ params }, api, extraOptions, baseQuery, signal) => {
          const result = await baseQuery(
            {
              method: 'GET',
              params: remapParams(params),
              signal,
              url: getUrl('get-customer-authorizations-list'),
            },
            api,
            extraOptions
          );

          if (result.error) {
            const isAbortError =
              result.error?.status === 'FETCH_ERROR' &&
              String(result.error?.error || '').includes('AbortError');

            if (!isAbortError) {
              api.dispatch(
                createSnack({
                  message:
                    result.error?.data?.title ||
                    result.error?.error ||
                    'Due to an error, your customer authorizations could not be retrieved. Please reload the page to try again.',
                  options: { variant: 'error' },
                  title: 'Action Not Completed',
                })
              );
            }

            return { error: result.error };
          }

          return { data: result.data };
        }
      ),
    }),
    // Single-row toggles use the bulk activation endpoints with one authorization ID.
    toggleCustomerAuthorizationActivation: mutation({
      onQueryStarted: createCacheUpdateHandler(
        loadingManagementApi,
        'getCustomerAuthorizationsList',
        (draft, { customerAuthorizationId, isActive }) => {
          const rows = draft.customerAuthorizations || [];
          const rowToUpdate = rows.find(
            row => row.customerAuthorizationId === customerAuthorizationId
          );

          if (!rowToUpdate) {
            return;
          }

          rowToUpdate.status = isActive ? 'Inactive' : 'Active';
        },
        { invalidateOnError: ['CustomerAuthorizationList'] }
      ),
      queryFn: async ({ customerAuthorizationId, isActive }, api, extraOptions, baseQuery) => {
        const result = await baseQuery(
          {
            body: { customerAuthorizationIds: [customerAuthorizationId] },
            method: 'POST',
            responseHandler: parseToggleActivationResponse,
            url: getUrl(`${isActive ? 'deactivate' : 'activate'}-customer-authorizations`),
          },
          api,
          extraOptions
        );

        if (result.error) {
          return { error: result.error };
        }

        const invalidResponses = Array.isArray(result.data?.invalidResponses)
          ? result.data.invalidResponses.filter(Boolean)
          : [];

        if (invalidResponses.length > 0) {
          return {
            error: {
              data: {
                detail: invalidResponses.join(' '),
                invalidResponses,
              },
              status: 400,
            },
          };
        }

        return { data: result.data };
      },
    }),
  }),
});

export const {
  useBulkActivateCustomerAuthorizationsMutation,
  useBulkDeactivateCustomerAuthorizationsMutation,
  useGetCustomerAuthorizationFilterValuesQuery,
  useLazyGetCustomerAuthorizationsExportListQuery,
  useGetCustomerAuthorizationsListQuery,
  useToggleCustomerAuthorizationActivationMutation,
} = customerAuthGridEndpoints;

export default customerAuthGridEndpoints;
